import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { 
  Anchor, 
  Ship, 
  Warehouse, 
  Truck, 
  Wrench, 
  ArrowRight,
  Calendar,
  MapPin,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HomePage = () => {
  const { t, language } = useLanguage();
  const [featuredBoats, setFeaturedBoats] = useState([]);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({ foundation_year: 1977, storage_spots: 200, employees: 12, brand_partners: 8 });
  const [siteImages, setSiteImages] = useState({ hero: '' });
  const [news, setNews] = useState([]);
  const [services, setServices] = useState([]);
  const [instaPosts, setInstaPosts] = useState([]);
  const [heroContent, setHeroContent] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [boatsRes, eventsRes, statsRes, imagesRes, newsRes, servicesRes, instaRes, heroRes] = await Promise.all([
          axios.get(`${API}/boats?featured=true`),
          axios.get(`${API}/events`),
          axios.get(`${API}/site-stats`),
          axios.get(`${API}/site-images`),
          axios.get(`${API}/news`),
          axios.get(`${API}/services`),
          axios.get(`${API}/instagram-posts`),
          axios.get(`${API}/hero-content`)
        ]);
        setFeaturedBoats(boatsRes.data.slice(0, 3));
        setEvents(eventsRes.data.slice(0, 4));
        setStats(statsRes.data);
        if (imagesRes.data.hero) {
          setSiteImages(imagesRes.data);
        }
        setNews(newsRes.data);
        setServices(servicesRes.data);
        setInstaPosts(instaRes.data.slice(0, 5));
        setHeroContent(heroRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Helper to get hero content by language
  const getHeroText = (field) => {
    if (!heroContent) return t(`hero.${field}`);
    const key = `${field}_${language}`;
    return heroContent[key] || heroContent[`${field}_it`] || t(`hero.${field}`);
  };

  const getDescription = (item) => {
    const key = `description_${language}`;
    return item[key] || item.description_it;
  };

  const getNewsTitle = (item) => {
    const key = `title_${language}`;
    return item[key] || item.title_it;
  };

  const getNewsContent = (item) => {
    const key = `content_${language}`;
    return item[key] || item.content_it;
  };

  const getServiceTitle = (item) => {
    const key = `title_${language}`;
    return item[key] || item.title_it;
  };

  const getServiceDescription = (item) => {
    const key = `description_${language}`;
    return item[key] || item.description_it;
  };

  // Format price with apostrophe as thousand separator (Swiss format)
  const formatPrice = (price) => {
    if (!price) return '0';
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  };

  // Map icon names to components
  const iconMap = {
    anchor: Ship,
    ship: Ship,
    warehouse: Warehouse,
    truck: Truck,
    wrench: Wrench
  };

  // Check if hero is video or image
  const isHeroVideo = siteImages.hero && (
    siteImages.hero.endsWith('.mp4') || 
    siteImages.hero.endsWith('.webm') || 
    siteImages.hero.endsWith('.ogg') ||
    siteImages.heroType === 'video'
  );

  const getHeroUrl = (url) => {
    if (!url) return '';
    // Cloudinary URLs (already absolute) - return as-is
    if (url.startsWith('https://res.cloudinary.com/')) return url;
    // Other absolute URLs - return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    // Legacy GridFS format
    if (url.startsWith('/api/media/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    if (url.startsWith('/api/uploads/')) return `${process.env.REACT_APP_BACKEND_URL}${url.replace('/api/uploads/', '/api/media/')}`;
    if (url.startsWith('/uploads/')) return `${process.env.REACT_APP_BACKEND_URL}/api/media/${url.replace('/uploads/', '')}`;
    return url;
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Video or Image */}
        <div className="absolute inset-0 bg-[#0B1221]">
          {siteImages.hero && (isHeroVideo ? (
            <video
              src={getHeroUrl(siteImages.hero)}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover object-center"
            />
          ) : (
            <img
              src={getHeroUrl(siteImages.hero)}
              alt="Cantiere Nautico Brusa"
              className="w-full h-full object-cover object-right"
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1221]/90 via-[#0B1221]/60 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pt-32 lg:pt-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-xl"
          >
            <p className="text-white text-sm md:text-base font-medium tracking-widest uppercase mb-4">
              {getHeroText('subtitle')}
            </p>
            <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
              {getHeroText('title')}
            </h1>
            <p className="text-white/90 text-base md:text-lg max-w-lg mb-10 leading-relaxed">
              {getHeroText('description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/services">
                <Button
                  data-testid="hero-cta-services"
                  className="bg-white text-[#0B1221] hover:bg-[#F5F5F7] rounded-full px-8 py-6 text-sm font-medium transition-all duration-300"
                >
                  {getHeroText('cta_primary')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/boats">
                <Button
                  data-testid="hero-cta-boats"
                  variant="outline"
                  className="bg-transparent border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-sm font-medium transition-all duration-300"
                >
                  {getHeroText('cta_secondary')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-6 h-10 rounded-full border-2 border-white/50 flex items-start justify-center p-2"
          >
            <div className="w-1 h-2 bg-white/70 rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section data-testid="stats-section" className="py-20 bg-[#0B1221]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stats.foundation_year}</div>
              <p className="text-[#86868B] text-sm uppercase tracking-wider">
                {language === 'it' ? 'Anno di fondazione' : language === 'de' ? 'Gründungsjahr' : language === 'fr' ? 'Année de fondation' : 'Year of foundation'}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stats.storage_spots}+</div>
              <p className="text-[#86868B] text-sm uppercase tracking-wider">
                {language === 'it' ? 'Posti deposito' : language === 'de' ? 'Lagerplätze' : language === 'fr' ? 'Places de dépôt' : 'Storage spots'}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stats.employees}</div>
              <p className="text-[#86868B] text-sm uppercase tracking-wider">
                {language === 'it' ? 'Specialisti' : language === 'de' ? 'Spezialisten' : language === 'fr' ? 'Spécialistes' : 'Specialists'}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stats.brand_partners}</div>
              <p className="text-[#86868B] text-sm uppercase tracking-wider">
                {language === 'it' ? 'Brand partner' : language === 'de' ? 'Markenpartner' : language === 'fr' ? 'Partenaires de marque' : 'Brand partners'}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <div className="text-4xl md:text-5xl font-bold text-[#FF3B30] mb-2">{stats.new_boats_stock || 0}</div>
              <p className="text-[#86868B] text-sm uppercase tracking-wider">
                {language === 'it' ? 'Barche nuove' : language === 'de' ? 'Neue Boote' : language === 'fr' ? 'Bateaux neufs' : 'New boats'}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <div className="text-4xl md:text-5xl font-bold text-[#FF3B30] mb-2">{stats.used_boats_stock || 0}</div>
              <p className="text-[#86868B] text-sm uppercase tracking-wider">
                {language === 'it' ? 'Barche usate' : language === 'de' ? 'Gebrauchte Boote' : language === 'fr' ? 'Bateaux occasion' : 'Used boats'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* News Widget Section */}
      {news.length > 0 && (
        <section data-testid="news-section" className="py-12 bg-gradient-to-b from-[#0B1221] to-[#1A2639]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#FF3B30]/30" />
              <h3 className="text-white text-sm font-semibold uppercase tracking-widest flex items-center gap-2">
                <span className="w-2 h-2 bg-[#FF3B30] rounded-full animate-pulse" />
                {language === 'it' ? 'News & Annunci' : language === 'de' ? 'News & Ankündigungen' : language === 'fr' ? 'Actualités' : 'News & Announcements'}
              </h3>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#FF3B30]/30" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {news.slice(0, 3).map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg overflow-hidden hover:bg-white/10 transition-all duration-300 group flex flex-col h-full"
                >
                  {item.image && (
                    <div className="aspect-[16/9] overflow-hidden flex-shrink-0">
                      <img 
                        src={getHeroUrl(item.image)}
                        alt={getNewsTitle(item)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-grow">
                    <h4 className="text-white font-semibold text-lg mb-2 line-clamp-2 flex-shrink-0">
                      {getNewsTitle(item)}
                    </h4>
                    <p className="text-[#86868B] text-sm mb-4 flex-grow" style={{ 
                      display: '-webkit-box',
                      WebkitLineClamp: 5,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minHeight: '100px'
                    }}>
                      {getNewsContent(item)}
                    </p>
                    <div className="flex-shrink-0 mt-auto">
                      {item.link && (
                        <a 
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-[#FF3B30] text-sm font-medium hover:underline"
                        >
                          {language === 'it' ? 'Scopri di più' : language === 'de' ? 'Mehr erfahren' : language === 'fr' ? 'En savoir plus' : 'Learn more'}
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services Section - Bento Grid */}
      <section data-testid="services-section" className="py-24 lg:py-32 bg-[#F5F5F7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0B1221] mb-4">
              {t('services.title')}
            </h2>
            <p className="text-[#6E6E73] text-base md:text-lg">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Ship;
              const imageUrl = getHeroUrl(service.image);
              const cardLink = service.key === 'vendita' ? '/boats' : '/services';
              
              return (
                <Link
                  key={service.id || service.key}
                  to={cardLink}
                  className="block"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    data-testid={`service-card-${service.key}`}
                    className="group relative bg-white border border-[#E5E5EA] overflow-hidden hover:border-[#0B1221] transition-colors duration-300 cursor-pointer h-full"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={imageUrl}
                        alt={getServiceTitle(service)}
                        className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-8">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-[#0B1221] rounded-full flex items-center justify-center group-hover:bg-[#FF3B30] transition-colors duration-300">
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="text-xl font-semibold text-[#0B1221]">
                          {getServiceTitle(service)}
                        </h3>
                      </div>
                      <p className="text-[#6E6E73] leading-relaxed">
                        {getServiceDescription(service)}
                      </p>
                    </div>
                  </motion.div>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button
                data-testid="services-cta"
                className="bg-[#0B1221] text-white hover:bg-[#1A2639] rounded-full px-8 py-4 text-sm font-medium"
              >
                {t('hero.cta')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Boats */}
      {featuredBoats.length > 0 && (
        <section data-testid="featured-boats-section" className="py-24 lg:py-32">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-4">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#0B1221] mb-2">
                  {t('boats.title')}
                </h2>
                <p className="text-[#6E6E73]">{t('boats.subtitle')}</p>
              </div>
              <Link to="/boats">
                <Button
                  data-testid="view-all-boats"
                  variant="outline"
                  className="rounded-full px-6 py-3 border-[#E5E5EA] hover:border-[#0B1221]"
                >
                  {t('boats.details')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredBoats.map((boat, index) => (
                <motion.div
                  key={boat.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`featured-boat-${boat.id}`}
                  className="group bg-white border border-[#E5E5EA] hover:border-[#0B1221] transition-colors duration-300"
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                      src={boat.images[0]}
                      alt={boat.name}
                      className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3">
                      <span className={`px-2.5 py-1 text-xs font-medium ${
                        boat.condition === 'Nuovo' 
                          ? 'bg-[#FF3B30] text-white' 
                          : 'bg-white text-[#0B1221]'
                      }`}>
                        {boat.condition === 'Nuovo' ? t('boats.new') : t('boats.used')}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-[#0B1221] text-lg mb-1">
                      {boat.name}
                    </h3>
                    <p className="text-[#FF3B30] text-sm font-medium mb-3">{boat.brand}</p>
                    
                    {/* Specifiche Tecniche */}
                    <div className="bg-[#F5F5F7] p-4 rounded mb-3">
                      <div className="text-xs text-[#6E6E73] uppercase tracking-wider mb-3 font-medium">
                        {language === 'it' ? 'Specifiche Tecniche' : language === 'de' ? 'Technische Daten' : language === 'fr' ? 'Spécifications' : 'Technical Specs'}
                      </div>
                      
                      {/* Grid 2x2 per le dimensioni */}
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-[#6E6E73]">{language === 'it' ? 'Anno' : 'Year'}</span>
                          <span className="text-[#0B1221] font-semibold">{boat.year}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-[#6E6E73]">{language === 'it' ? 'Lunghezza' : 'Length'}</span>
                          <span className="text-[#0B1221] font-semibold">{Number(boat.length_m).toFixed(2)} m</span>
                        </div>
                        {boat.width_m && (
                          <div className="flex justify-between items-center">
                            <span className="text-[#6E6E73]">{language === 'it' ? 'Larghezza' : 'Width'}</span>
                            <span className="text-[#0B1221] font-semibold">{Number(boat.width_m).toFixed(2)} m</span>
                          </div>
                        )}
                        {boat.capacity && (
                          <div className="flex justify-between items-center">
                            <span className="text-[#6E6E73]">{language === 'it' ? 'Persone' : 'Capacity'}</span>
                            <span className="text-[#0B1221] font-semibold">{boat.capacity}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Motore */}
                      <div className="border-t border-[#E5E5EA] pt-3 mt-3">
                        <div className="text-xs text-[#6E6E73] uppercase tracking-wider mb-2">
                          {language === 'it' ? 'Motorizzazione' : 'Engine'}
                        </div>
                        <div className="text-sm text-[#0B1221] font-semibold">
                          {boat.num_engines > 1 ? `${boat.num_engines}x ` : ''}
                          {boat.engine_brand || ''}{boat.engine_brand && boat.engine ? ' ' : ''}{boat.engine || ''}
                          {boat.engine_power ? ` - ${boat.engine_power} CV` : ''}
                        </div>
                        {boat.engine_hours && (
                          <div className="text-xs text-[#6E6E73] mt-1">
                            {language === 'it' ? 'Ore motore' : 'Hours'}: {formatPrice(boat.engine_hours)}h
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center pt-2 border-t border-[#E5E5EA]">
                      <span className="font-bold text-[#0B1221] text-lg">
                        CHF {formatPrice(boat.price)}
                      </span>
                      <Link to="/boats">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full text-xs px-3 py-1 border-[#E5E5EA] hover:border-[#0B1221] hover:bg-[#0B1221] hover:text-white"
                        >
                          {t('boats.details')}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Events Section */}
      {events.length > 0 && (
        <section data-testid="events-section" className="py-24 lg:py-32 bg-[#0B1221] relative overflow-hidden">
          {/* Background Image Watermark */}
          {siteImages.events_bg && (
            <div className="absolute inset-0">
              <img
                src={getHeroUrl(siteImages.events_bg)}
                alt=""
                aria-hidden="true"
                className="w-full h-full object-cover opacity-30"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-[#0B1221]/30 to-[#0B1221]/70" />
            </div>
          )}
          
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {t('events.title')}
              </h2>
              <p className="text-[#86868B]">{t('events.subtitle')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {events.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`event-card-${event.id}`}
                  className="bg-white/10 backdrop-blur-sm border border-white/20 p-6 hover:bg-white/20 transition-colors duration-300"
                >
                  <div className="flex items-center gap-2 text-[#FF3B30] mb-4">
                    <Calendar className="w-4 h-4" />
                    <span className="font-mono text-sm">
                      {event.start_date.split('-').reverse().join('.')}
                    </span>
                  </div>
                  <h3 className="font-semibold text-white text-lg mb-2">
                    {event.name}
                  </h3>
                  <div className="flex items-center gap-2 text-[#86868B] text-sm mb-4">
                    <MapPin className="w-4 h-4" />
                    {event.location}
                  </div>
                  <p className="text-[#86868B] text-sm mb-4">
                    {getDescription(event)}
                  </p>
                  {event.url && (
                    <a
                      href={event.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-white hover:text-[#FF3B30] font-medium transition-colors"
                    >
                      {t('events.visitWebsite')}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Instagram Feed - Elfsight Widget */}
      <section data-testid="instagram-section" className="py-20 lg:py-28 bg-[#F5F5F7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <a href="https://www.instagram.com/nauticabrusa/" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[#6E6E73] hover:text-[#0B1221] transition-colors mb-4">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              @nauticabrusa
            </a>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1221]">
              {language === 'it' ? 'Seguici su Instagram' : language === 'de' ? 'Folge uns auf Instagram' : language === 'fr' ? 'Suivez-nous sur Instagram' : 'Follow us on Instagram'}
            </h2>
          </div>
          {/* Elfsight Instagram Feed Widget */}
          <div className="elfsight-app-459577bb-afa2-4f03-a497-317d22c26767" data-elfsight-app-lazy></div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 lg:py-32 bg-[#0B1221]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 bg-[#FF3B30] rounded-full flex items-center justify-center mx-auto mb-8">
              <Anchor className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              {t('quote.title')}
            </h2>
            <p className="text-[#86868B] text-lg max-w-2xl mx-auto mb-10">
              {t('quote.subtitle')}
            </p>
            <Link to="/quote">
              <Button
                data-testid="cta-quote-button"
                className="bg-white text-[#0B1221] hover:bg-[#F5F5F7] rounded-full px-10 py-6 text-sm font-medium transition-all duration-300"
              >
                {t('nav.quote')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
